import React, { useEffect, useState } from "react";
import { Box, Button, Card, Grid2 as Grid, Typography } from "@mui/material";
import DeleteIcon from "../../assets/deleteIcon.png";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Breadcrumbs from "../../common/Breadcrumbs";
import ConfirmationDialog from "../../common/confirmationDialog/ConfirmationDialog";
import DataTable from "../../common/DataTable";
import { defaultUserData } from "../../constants/defaultValues";
import { User } from "../../constants/types";
import { userDetails } from "../../schemas/userDetails";
import ControlledTextField from "../../common/ControlledTextField";
import { api } from "../../api/api";
import { endpoints } from "../../constants/endpoints";
import Color from "../../theme/Colours";
import { labelStyle, titleStyle } from "./userManagementStyles";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  addUser,
  removeUser,
  setUsers,
} from "../../redux/features/slices/userSlice";
import moment from "moment";
import Toast from "../../common/Toast";

const UserManagement = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector((state) => state.users.users);

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: defaultUserData,
    resolver: yupResolver(userDetails),
    mode: "onSubmit",
  });

  const fieldValues = watch();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const handleGetUsers = async () => {
    setToastMessage("Failed to fetch users");
    setError(true);
    try {
      const response = await api.get(endpoints.getUsers);

      if (response.data) {
        const transformedData = response.data.data.map((user: any) => ({
          ...user,
          firstName: user.name.split(" ")[0],
          lastName: user.name.split(" ")[1] || "",
          createdDate: moment(user.createdAt).format("DD MMMM YY hh:mm:ss A"),
        }));
        dispatch(setUsers(transformedData));
      }
      setError(false);
      setToastMessage("Users fetched successfully");
    } catch (error) {
      console.log("Failed to get data, Reason", error);
    }
    setShowToast(true);
  };

  const handleAddUser = async () => {
    setToastMessage("Failed to add user");
    setError(true);

    const values = {
      name: fieldValues.firstName + " " + fieldValues.lastName,
      email: fieldValues.email,
      phone_number: "0",
      country_code: "USA",
    };
    try {
      const response = await api.post(endpoints.addUser, values);

      if (response.status === 201) {
        const newUser = {
          id: response.data.data.id,
          firstName: fieldValues.firstName,
          lastName: fieldValues.lastName,
          email: fieldValues.email,
          createdDate: moment(response.data.createdAt).format(
            "DD MMMM YY hh:mm:ss A"
          ),
          phone_number: response.data.phone_number,
        };
        dispatch(addUser(newUser));
        setError(false);
        setToastMessage("User added successfully");
        reset();
      }
    } catch (error: any) {
      if (error.response.data.error.message === "User Exists") {
        setToastMessage("The email ID already exists.");
      }
      console.error(error);
    }
    setShowToast(true);
  };

  const handleDeleteUser = async (id: string) => {
    setUserToDelete(id);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    setError(true);
    setToastMessage("Failed to delete user");

    setShowDeleteDialog(false);
    const { email, id } = users.find((user) => user.id === userToDelete) || {};
    try {
      const response = await api.delete(endpoints.deleteUser, {
        data: {
          id: id,
          email: email,
        },
      });
      if (response.status === 200) {
        dispatch(removeUser(id!));
        setError(false);
        setToastMessage("User deleted successfully");
      }
    } catch (err) {
      console.error("Failed to delete:", err);
    }
    setShowToast(true);
    setUserToDelete(null);
  };

  // Columns for the DataTable
  const columns: {
    label: string;
    field: string;
    align?: "left" | "center" | "right";
    renderCell?: (row: User) => JSX.Element;
  }[] = [
    {
      label: "Name",
      field: "name",
      align: "left",
      renderCell: (row: User) => (
        <Typography sx={{ color: Color.linkColor }}>
          {`${row.firstName} ${row.lastName}`}
        </Typography>
      ),
    },
    {
      label: "Created Date",
      field: "createdDate",
      align: "center",
    },
    {
      label: "Email",
      field: "email",
      align: "center",
    },
    {
      label: "Delete",
      field: "delete",
      align: "left",
      renderCell: (row: User) => (
        <img
          src={DeleteIcon}
          onClick={() => row.id && handleDeleteUser(row.id)}
          aria-label="Delete"
          style={{ cursor: "pointer" }}
        />
      ),
    },
  ];

  useEffect(() => {
    handleGetUsers();
  }, []);

  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  return (
    <>
      <Breadcrumbs
        paths={["User Management"]}
        links={["/seller/user-management"]}
      />

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <Box
        display="flex"
        flexDirection={"column"}
        justifyContent="center"
        alignItems="center"
        pt={"10px"}
      >
        <Box width={"100%"}>
          <Card sx={{ p: "20px" }}>
            <Box>
              <Typography sx={titleStyle}>User Details</Typography>

              <Grid container spacing={2} mt={"60px"}>
                {/* Name Label */}
                <Grid size={[12, 4]}>
                  <Typography sx={labelStyle}>First Name</Typography>

                  <ControlledTextField
                    control={control}
                    name="firstName"
                    placeholder=""
                  />
                </Grid>

                <Grid size={[12, 4]}>
                  <Typography sx={labelStyle}>Last Name</Typography>

                  <ControlledTextField
                    control={control}
                    name="lastName"
                    placeholder=""
                  />
                </Grid>

                {/* Email Label */}
                <Grid size={[12, 4]}>
                  <Typography sx={labelStyle}>Email ID</Typography>
                  <ControlledTextField
                    control={control}
                    name="email"
                    placeholder=""
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid size={[0, 6]} />
                <Grid container size={[12, 6]} spacing={"25px"} mt={"40px"}>
                  <Grid size={[12, 6]}>
                    <Button
                      sx={{ borderColor: "#333333" }}
                      variant="outlined"
                      onClick={() => reset()}
                    >
                      Reset
                    </Button>
                  </Grid>
                  <Grid size={[12, 6]}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit(handleAddUser)}
                    >
                      Add User
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Box mt={"80px"} mb={"60px"}>
              <Typography sx={titleStyle}>Current Use List</Typography>
            </Box>

            {/* Table to display list of users */}
            <Box sx={{ mb: "60px" }}>
              <DataTable
                columns={columns}
                data={users}
                emptyStateMessage="No users found."
                rowsPerPage={5}
              />
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        show={showDeleteDialog}
        setShow={setShowDeleteDialog}
        title="Are you sure you want to delete?"
        buttonTitle="Yes, I Am Sure"
        buttonAction={confirmDeleteUser}
        // error={true}
      />
    </>
  );
};

export default UserManagement;
